'use client';

import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useId, useRef, useState } from 'react';

import styles from './realty-detail-info.module.css';

type RealtyDetailDescriptionProps = {
  description: string;
  expandLabel: string;
  collapseLabel: string;
};

export const RealtyDetailDescription = ({
  description,
  expandLabel,
  collapseLabel,
}: RealtyDetailDescriptionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLElement>(null);
  const descriptionId = useId();

  useEffect(() => {
    const element = descriptionRef.current;
    if (!element) return;

    const measure = () => {
      const styles = getComputedStyle(element);
      const lineHeight = Number.parseFloat(styles.lineHeight);
      const visibleLines = Number.parseInt(styles.getPropertyValue('--description-lines'), 10) || 6;
      const collapsedHeight = Number.isFinite(lineHeight)
        ? lineHeight * visibleLines
        : element.clientHeight;
      setIsOverflowing(element.scrollHeight > collapsedHeight + 0.5);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [description]);

  return (
    <div className={styles.descriptionBlock} data-eli-part="description-block">
      <HtmlRendererRaw
        ref={descriptionRef}
        id={descriptionId}
        html={description}
        className={cn(styles.description, !isExpanded && styles.descriptionCollapsed)}
        data-eli-part="description"
      />
      {isOverflowing && (
        <button
          type="button"
          className={styles.descriptionToggle}
          aria-controls={descriptionId}
          aria-expanded={isExpanded}
          data-eli-part="description-toggle"
          onClick={() => setIsExpanded(value => !value)}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
};
